import type { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { getDuplicatesActivateCall } from 'reactApp/api/duplicates/activate/DuplicatesActivateAPICall';
import { getDuplicatesCountCall } from 'reactApp/api/duplicates/count/GetDuplicatesCountAPICall.makeRequest';
import { getDuplicatesListCall } from 'reactApp/api/duplicates/list/GetDuplicatesListAPICall.makeRequest';
import { getDuplicatesPreviewCall } from 'reactApp/api/duplicates/preview/GetDuplicatesPreviewAPICall.makeRequest';
import {
    duplicatesActivate,
    duplicatesLoadMoreRequest,
    duplicatesLoadMoreSuccess,
    loadDuplicatesCountFail,
    loadDuplicatesCountRequest,
    loadDuplicatesCountSuccess,
    loadDuplicatesListFail,
    loadDuplicatesListRequest,
    loadDuplicatesListSuccess,
    loadDuplicatesPreviewFail,
    loadDuplicatesPreviewRequest,
    loadDuplicatesPreviewSuccess,
    removeDuplicateFromContextMenuStart,
    removeDuplicateFromViewerStart,
    removeDuplicatesAllStart,
    removeDuplicatesAllWithLimitStart,
    removeDuplicatesGroupStart,
    showBestPhotoTooltip,
    showBestPhotoTooltipStart,
} from 'reactApp/modules/duplicates/duplicates.actions';
import {
    DUPLICATES_SUCCESS_CLEAR_ACTION__NAME,
    REMOVE_ALL_DUPLICATES_TOOLTIP_DURATION,
    REMOVE_ALL_DUPLICATES_TOOLTIP_ID,
} from 'reactApp/modules/duplicates/duplicates.constants';
import {
    getDuplicateGroup,
    getDuplicatesCursor,
    getDuplicatesGroupById,
    getDuplicatesGroupsCount,
    getDuplicatesGroupsList,
} from 'reactApp/modules/duplicates/duplicates.selectors';
import type { IDuplicateItem, IRemoveDuplicateFromViewer } from 'reactApp/modules/duplicates/duplicates.types';
import { handleRemoveDuplicateItemsRequest } from 'reactApp/modules/modifying/sagas/removeItems.saga';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { closeViewer } from 'reactApp/modules/viewer/viewer.module';
import {
    getDuplicatesMailSpacePromoModalShown,
    renderDuplicatesMailSpacePromoModal,
    setDuplicatesMailSpacePromoModalShown,
} from 'reactApp/ui/Duplicates/DuplicatesMailSpacePromoModal/DuplicatesMailSpacePromoModal.helpers';
import {
    getDuplicatesRemovedGroupsCount,
    setDuplicatesRemovedGroupsCount,
} from 'reactApp/ui/Duplicates/DuplicatesRemoveLimitModal/DuplicatesRemoveLimitModal.helpers';
import { sendDwh, sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { call, delay, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { select } from 'typed-redux-saga';

function* handleLoadDuplicatesList() {
    try {
        const { data } = yield getDuplicatesListCall();
        yield put(loadDuplicatesListSuccess(data));
    } catch (error) {
        yield put(loadDuplicatesListFail());
        logger.error(error);
    }
}

function* handleLoadDuplicatesPreview() {
    try {
        const { data } = yield getDuplicatesPreviewCall();
        yield put(loadDuplicatesPreviewSuccess(data));
    } catch (error) {
        yield put(loadDuplicatesPreviewFail());
        logger.error(error);
    }
}

function* handleLoadDuplicatesCount() {
    try {
        const { data } = yield getDuplicatesCountCall();
        yield put(loadDuplicatesCountSuccess(data));
    } catch (error) {
        yield put(loadDuplicatesCountFail());
        logger.error(error);
    }
}

function* handleLoadDuplicatesCountSuccess() {
    const storage = yield* select(getCurrentStorage);

    if (storage === EStorageType.albums) {
        const count = yield* select(getDuplicatesGroupsCount);
        if (count > 0) {
            yield put(loadDuplicatesPreviewRequest());
        }
    }
}

function* handleDuplicatesLoadMore() {
    const cursor = yield* select(getDuplicatesCursor);

    if (!cursor) {
        return;
    }

    const { data } = yield getDuplicatesListCall(cursor);
    yield put(duplicatesLoadMoreSuccess(data));
}

function* handleShowBestPhotoTooltipStart() {
    yield delay(REMOVE_ALL_DUPLICATES_TOOLTIP_DURATION);

    const storage = yield* select(getCurrentStorage);
    const { isDuplicates } = getStorage(storage);
    if (isDuplicates) {
        yield put(showBestPhotoTooltip());
        storeHelper.setValue(REMOVE_ALL_DUPLICATES_TOOLTIP_ID, true);
    }
}

function* handleRemoveDuplicatesGroupStart(actionData) {
    const { groupId } = actionData.payload;

    const group = yield* select(getDuplicatesGroupById, groupId);
    if (!group) {
        return;
    }

    const bestPhoto = group.best_photo;
    const itemsToDelete: IDuplicateItem[] = [];

    for (const id of group.files) {
        if (id !== bestPhoto) {
            const item: IDuplicateItem = yield select(getItemById, id);
            itemsToDelete.push(item);
        }
    }

    const isCorp = yield* select(UserSelectors.isCorpUser);
    const isPaid = yield* select(UserSelectors.isPaidUser);
    const duplicatesLimitEnabled = !isCorp && !isPaid;

    const result = yield call(handleRemoveDuplicateItemsRequest, itemsToDelete);

    if (result) {
        const spacePromoModalShown = yield put(getDuplicatesMailSpacePromoModalShown());
        if (!spacePromoModalShown && duplicatesLimitEnabled) {
            yield put(setDuplicatesMailSpacePromoModalShown());
            renderDuplicatesMailSpacePromoModal();
        }

        sendXray(['dp-suc-clear-group']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: DUPLICATES_SUCCESS_CLEAR_ACTION__NAME,
            clear_type: 'group',
            size_files: itemsToDelete.reduce((accumulator, item) => accumulator + item.size, 0),
            count_files: itemsToDelete.length,
        });
    }

    if (duplicatesLimitEnabled) {
        const oldRemovedGroupsCount = yield put(getDuplicatesRemovedGroupsCount()) || 0;
        const newRemovedGroupsCount = oldRemovedGroupsCount + 1;
        yield put(setDuplicatesRemovedGroupsCount(newRemovedGroupsCount));
    }
}

function* handleRemoveDuplicatesAllStart() {
    const groups = yield* select(getDuplicatesGroupsList);
    const groupsCount = Object.keys(groups).length;

    const itemsToDelete: IDuplicateItem[] = [];

    for (const group of Object.values(groups)) {
        const bestPhoto = group.best_photo;

        for (const id of group.files) {
            if (id !== bestPhoto) {
                const item = yield* select(getItemById, id);
                itemsToDelete.push(item as IDuplicateItem);
            }
        }
    }

    const isCorp = yield* select(UserSelectors.isCorpUser);
    const isPaid = yield* select(UserSelectors.isPaidUser);
    const duplicatesLimitEnabled = !isCorp && !isPaid;

    const result = yield call(handleRemoveDuplicateItemsRequest, itemsToDelete);

    if (result) {
        const spacePromoModalShown = yield put(getDuplicatesMailSpacePromoModalShown());
        if (!spacePromoModalShown && duplicatesLimitEnabled) {
            yield put(setDuplicatesMailSpacePromoModalShown());
            renderDuplicatesMailSpacePromoModal();
        }

        sendXray(['dp-suc-clear-all']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: DUPLICATES_SUCCESS_CLEAR_ACTION__NAME,
            clear_type: 'all',
            groups_count: groups.length,
            size_files: itemsToDelete.reduce((accumulator, item) => accumulator + item.size, 0),
            count_files: itemsToDelete.length,
        });

        if (duplicatesLimitEnabled) {
            const oldRemovedGroupsCount = yield put(getDuplicatesRemovedGroupsCount()) || 0;
            const newRemovedCount = oldRemovedGroupsCount + groupsCount;
            yield put(setDuplicatesRemovedGroupsCount(newRemovedCount));
        }
    }
}

function* handleRemoveDuplicatesGroupsWithLimitStart(action) {
    const { count, showPromo } = action.payload;

    const groups = yield* select(getDuplicatesGroupsList);

    const itemsToDelete: IDuplicateItem[] = [];

    for (const group of Object.values(groups).slice(0, count)) {
        const bestPhoto = group.best_photo;

        for (const id of group.files) {
            if (id !== bestPhoto) {
                const item = yield* select(getItemById, id);
                itemsToDelete.push(item as IDuplicateItem);
            }
        }
    }

    const isCorp = yield* select(UserSelectors.isCorpUser);
    const isPaid = yield* select(UserSelectors.isPaidUser);
    const duplicatesLimitEnabled = !isCorp && !isPaid;

    const result = yield call(handleRemoveDuplicateItemsRequest, itemsToDelete);

    if (result) {
        const spacePromoModalShown = yield put(getDuplicatesMailSpacePromoModalShown());
        if (!spacePromoModalShown && duplicatesLimitEnabled && showPromo) {
            yield put(setDuplicatesMailSpacePromoModalShown());
            renderDuplicatesMailSpacePromoModal();
        }

        sendXray(['dp-suc-clear-all-with-lim']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: DUPLICATES_SUCCESS_CLEAR_ACTION__NAME,
            clear_type: 'all-with-limit',
            groups_count: count,
            size_files: itemsToDelete.reduce((accumulator, item) => accumulator + item.size, 0),
            count_files: itemsToDelete.length,
        });

        if (duplicatesLimitEnabled) {
            const oldRemovedGroupsCount = yield put(getDuplicatesRemovedGroupsCount()) || 0;
            const newRemovedCount = oldRemovedGroupsCount + count;
            yield put(setDuplicatesRemovedGroupsCount(newRemovedCount));
        }
    }
}

function* handleRemoveDuplicateFromViewerStart(action: PayloadAction<IRemoveDuplicateFromViewer>) {
    const { item } = action.payload;

    const group = yield* select(getDuplicateGroup, item.groupId);
    const shouldDeleteGroup = group.files.length === 2;

    const itemsToDelete = [item];

    const isCorp = yield* select(UserSelectors.isCorpUser);
    const isPaid = yield* select(UserSelectors.isPaidUser);
    const duplicatesLimitEnabled = !isCorp && !isPaid;

    const result = yield call(handleRemoveDuplicateItemsRequest, itemsToDelete);

    if (result) {
        const spacePromoModalShown = yield put(getDuplicatesMailSpacePromoModalShown());
        if (!spacePromoModalShown && duplicatesLimitEnabled) {
            yield put(setDuplicatesMailSpacePromoModalShown());
            renderDuplicatesMailSpacePromoModal();
        }

        sendXray(['dp-suc-delete-viewer']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: DUPLICATES_SUCCESS_CLEAR_ACTION__NAME,
            clear_type: 'viewer',
            size_files: item.size,
            count_files: 1,
        });

        yield put(closeViewer());

        if (duplicatesLimitEnabled && shouldDeleteGroup) {
            const oldRemovedGroupsCount = yield put(getDuplicatesRemovedGroupsCount()) || 0;
            const newRemovedCount = oldRemovedGroupsCount + 1;
            yield put(setDuplicatesRemovedGroupsCount(newRemovedCount));
        }
    }
}

function* handleRemoveDuplicateFromContextMenuStart(action: PayloadAction<IRemoveDuplicateFromViewer>) {
    const { item } = action.payload;

    const group = yield* select(getDuplicateGroup, item.groupId);
    const shouldDeleteGroup = group.files.length === 2;

    const itemsToDelete = [item];

    const isCorp = yield* select(UserSelectors.isCorpUser);
    const isPaid = yield* select(UserSelectors.isPaidUser);
    const duplicatesLimitEnabled = !isCorp && !isPaid;

    const result = yield call(handleRemoveDuplicateItemsRequest, itemsToDelete);

    if (result) {
        const spacePromoModalShown = yield put(getDuplicatesMailSpacePromoModalShown());
        if (!spacePromoModalShown && duplicatesLimitEnabled) {
            yield put(setDuplicatesMailSpacePromoModalShown());
            renderDuplicatesMailSpacePromoModal();
        }

        sendXray(['dp-suc-delete-ctxt-menu']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: DUPLICATES_SUCCESS_CLEAR_ACTION__NAME,
            clear_type: 'context-menu',
            size_files: item.size,
            count_files: 1,
        });

        if (duplicatesLimitEnabled && shouldDeleteGroup) {
            const oldRemovedGroupsCount = yield put(getDuplicatesRemovedGroupsCount()) || 0;
            const newRemovedCount = oldRemovedGroupsCount + 1;
            yield put(setDuplicatesRemovedGroupsCount(newRemovedCount));
        }
    }
}

function* handleDuplicatesActivate() {
    try {
        const response = yield call(getDuplicatesActivateCall);
        if (response.status === 200) {
            sendXray(['dp-activation-suc']);
            sendDwh({ eventCategory: ECategoryGa.duplicates, action: 'activate' });
        }
    } catch (error) {
        sendXray(['dp-activation-err']);
        logger.error(error);
    }
}

export function* watchDuplicates() {
    yield takeEvery(loadDuplicatesListRequest.toString(), handleLoadDuplicatesList);
    yield takeEvery(loadDuplicatesPreviewRequest.toString(), handleLoadDuplicatesPreview);
    yield takeEvery(loadDuplicatesCountRequest.toString(), handleLoadDuplicatesCount);
    yield takeEvery(loadDuplicatesCountSuccess.toString(), handleLoadDuplicatesCountSuccess);
    yield takeEvery(showBestPhotoTooltipStart.toString(), handleShowBestPhotoTooltipStart);
    yield takeEvery(removeDuplicatesGroupStart.toString(), handleRemoveDuplicatesGroupStart);
    yield takeEvery(removeDuplicatesAllStart.toString(), handleRemoveDuplicatesAllStart);
    yield takeEvery(removeDuplicatesAllWithLimitStart.toString(), handleRemoveDuplicatesGroupsWithLimitStart);
    yield takeEvery(removeDuplicateFromViewerStart.toString(), handleRemoveDuplicateFromViewerStart);
    yield takeLatest(duplicatesLoadMoreRequest.toString(), handleDuplicatesLoadMore);
    yield takeEvery(removeDuplicateFromContextMenuStart.toString(), handleRemoveDuplicateFromContextMenuStart);
    yield takeEvery(duplicatesActivate.toString(), handleDuplicatesActivate);
}
